import React, { useState, useRef, useEffect } from 'react';

/**
 * Rotary knob used by the Kontakt-style controls (velocity, close, over, room…).
 * Fully controlled: parent owns `value` and re-renders on `onChange`.
 * Extracted from BatucadaRio.jsx.
 */
export const Knob = ({ value, onChange, label, min = 0, max = 100, size = 56 }) => {
  const knobRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const startValue = useRef(0);

  const rotation = ((value - min) / (max - min)) * 270 - 135;

  const handleMouseDown = (e) => {
    setIsDragging(true);
    startY.current = e.clientY;
    startValue.current = value;
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const delta = startY.current - e.clientY;
      const newValue = Math.min(max, Math.max(min, startValue.current + delta * 0.5));
      onChange(Math.round(newValue));
    };

    const handleMouseUp = () => setIsDragging(false);

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, min, max, onChange]);

  return (
    <div className="flex flex-col items-center gap-1">
      <div
        ref={knobRef}
        className="relative cursor-ns-resize select-none"
        style={{ width: size, height: size }}
        onMouseDown={handleMouseDown}
      >
        <div
          className="absolute inset-0 rounded-full bg-gradient-to-b from-[#4a4a4a] to-[#2a2a2a] border border-[#555] shadow-lg"
          style={{
            boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.1), 0 4px 8px rgba(0,0,0,0.4)',
          }}
        />
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ transform: `rotate(${rotation}deg)` }}
        >
          <div
            className="w-1 h-5 bg-amber-400 rounded-full"
            style={{ marginTop: '-12px' }}
          />
        </div>
        <div className="absolute inset-3 rounded-full bg-gradient-to-b from-[#3a3a3a] to-[#1a1a1a]" />
      </div>
      <span className="text-[10px] text-gray-400 font-medium tracking-wide">{label}</span>
      <span className="text-xs text-amber-400 font-mono">{value.toFixed(1)}</span>
    </div>
  );
};

export default Knob;
